import { AchCompanyModelDto as BoAchCompanyModelDto } from '@treasury/api/bo';
import { AchCompanyModelDto as ConsumerAchCompanyModelDto } from '@treasury/api/channel';
import { formatUsdCurrency } from '@treasury/utils';
import { DomainEntity } from '../../shared';
import { SecCode } from '../product-features/entities/sec-code.entity';

export enum IsActive {
    Active = 'Active',
    Inactive = 'Inactive',
}

export enum AllowUnbalancedPayments {
    Allowed = 'Allowed',
    NotAllowed = 'Not allowed',
}

export class AchCompany extends DomainEntity<BoAchCompanyModelDto | ConsumerAchCompanyModelDto> {
    constructor(dto: BoAchCompanyModelDto | ConsumerAchCompanyModelDto) {
        super(dto);
        this._secCodes = dto.secCodes?.map(code => new SecCode(code)) || [];
    }

    private _secCodes: SecCode[] = [];

    public createDefault(): this {
        throw new Error('Method not implemented.');
    }

    public get companyName() {
        return this.dto.companyName;
    }

    public get companyId() {
        return this.dto.companyId;
    }

    public get batchBalanceRequirements() {
        return this.dto.batchBalanceRequirements;
    }

    public get prefundingDays() {
        return this.dto.prefundingDays;
    }

    public get id() {
        return this.dto.id;
    }

    /**
     * Gets the account identifier from the underlying DTO.
     *
     * The account ID is the same as the company ID in the context of ACH accounts. It is here purely for
     * the sake of use in the TransferTable base component.
     *
     * This accessor returns the DTO's `id` property, which represents the unique
     * identifier for the account associated with this entity.
     *
     * @returns The account identifier from the DTO.
     */
    public get accountId() {
        return this.dto.id;
    }

    public get offsetAccountNumber() {
        return this.dto.offsetAccountNumber as string | undefined;
    }

    public get isActiveString() {
        return this.dto.isActive ? IsActive.Active : IsActive.Inactive;
    }

    public get isActive() {
        return this.dto.isActive;
    }

    public set isActive(value: boolean) {
        this.dto.isActive = value;
    }

    public get secCodes() {
        return this._secCodes;
    }

    public set secCodes(value: SecCode[]) {
        this._secCodes = value;
        this.dto.secCodes = value.map(code => code.toDto());
    }

    public get secCodesFormatted() {
        return this.dto.secCodes && this.dto.secCodes.length
            ? this.dto.secCodes.map(code => code.code).join(', ')
            : undefined;
    }

    public get coreSecCode(): string | undefined {
        // The coreSecCode prop only exists in BoAchCompanyModelDto, not in ConsumerAchCompanyModelDto
        if ('coreSecCode' in this.dto) {
            return this.dto.coreSecCode;
        }
        return undefined;
    }

    public get entryDescription() {
        return this.dto.entryDescription;
    }

    public get discretionaryData() {
        return this.dto.discretionaryData;
    }

    public get totalDailyLimit() {
        return this.dto.totalDailyLimit;
    }

    public get totalDailyLimitFormatted() {
        // format totalDailyLimit as currency
        return this.totalDailyLimit ? formatUsdCurrency(this.totalDailyLimit) : undefined;
    }

    public get notOnUsTransactionTypes() {
        return this.dto.notOnUsTransactionTypes;
    }

    public get onUsTransactionsRequired() {
        return this.dto.onUsTransactionsRequired;
    }

    public get onUsAmountRequiredPerBatch() {
        return this.dto.onUsAmountRequiredPerBatch;
    }

    public get allowUnbalancedPaymentsString() {
        return this.dto.allowUnbalancedPayments
            ? AllowUnbalancedPayments.Allowed
            : AllowUnbalancedPayments.NotAllowed;
    }

    public get allowUnbalancedPayments() {
        return this.dto.allowUnbalancedPayments ?? false;
    }

    public set allowUnbalancedPayments(value: boolean) {
        this.dto.allowUnbalancedPayments = value;
    }
}
